import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export type MvpBrief = {
  id: string;
  user_id: string;
  idea: string;
  pages: string[];
  tech_pref: string;
  audience: string;
  monetization: string;
  spec_markdown: string;
  build_id: string | null;
  build_slug: string | null;
  build_status: string;
  created_at: string;
  updated_at: string;
};

export type MvpBriefInput = {
  idea: string;
  pages: string[];
  tech_pref: string;
  audience: string;
  monetization: string;
  spec_markdown?: string;
};

export type MvpBriefPatch = Partial<{
  spec_markdown: string;
  build_id: string | null;
  build_slug: string | null;
  build_status: string;
}>;

/** Fetch the signed-in user's MVP brief history, newest first.
 *  Disabled for anon users so we don't spam unauthorized requests. */
export const useMvpBriefs = () => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["mvp-briefs", user?.id ?? "anon"],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("mvp_briefs")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as MvpBrief[];
    },
  });
};

/** Insert a new brief row. Returns the saved row so callers can stash
 *  its id and patch the spec / build linkage as the wizard progresses. */
export const useCreateMvpBrief = () => {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: MvpBriefInput) => {
      if (!user) throw new Error("Sign in to save MVP briefs");
      const { data, error } = await supabase
        .from("mvp_briefs")
        .insert({
          user_id: user.id,
          idea: input.idea,
          pages: input.pages,
          tech_pref: input.tech_pref,
          audience: input.audience,
          monetization: input.monetization,
          spec_markdown: input.spec_markdown ?? "",
          build_status: "draft",
        })
        .select()
        .single();
      if (error) throw error;
      return data as MvpBrief;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mvp-briefs"] });
    },
  });
};

/** Patch a brief — used to attach the streamed spec, link the resulting
 *  build, and update lifecycle status (`generating`, `ready`, `error`). */
export const useUpdateMvpBrief = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: MvpBriefPatch }) => {
      const { data, error } = await supabase
        .from("mvp_briefs")
        .update(patch)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data as MvpBrief;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mvp-briefs"] });
    },
  });
};

export const useDeleteMvpBrief = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("mvp_briefs").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mvp-briefs"] }),
  });
};